// src/views/portal/ReservasPortal.jsx
// Calendário de Reservas de Áreas Comuns no Portal do Condômino

import React, { useState, useEffect, useCallback, useMemo } from "react";
import portalService from "../../services/portalService";
import ConfirmModal from "../../components/ui/ConfirmModal";

const DIAS_SEMANA = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho",
               "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

const statusColor = {
  Pendente:   { bg:"#FEF9C3", txt:"#854D0E" },
  Confirmada: { bg:"#DCFCE7", txt:"#166534" },
  Cancelada:  { bg:"#F1F5F9", txt:"#64748B" },
  Realizada:  { bg:"#EFF6FF", txt:"#1D4ED8" },
};

function fmtD(d) {
  if (!d) return "—";
  try {
    const s = d instanceof Date ? d.toISOString().split("T")[0] : String(d).split("T")[0];
    const [y,m,dd] = s.split("-");
    return `${dd}/${m}/${y}`;
  } catch { return String(d); }
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0,10);
}

function diffDays(date) {
  const d = new Date(String(date).split("T")[0] + "T12:00:00");
  const t = new Date(); t.setHours(0,0,0,0);
  return Math.ceil((d - t) / (1000*60*60*24));
}

export default function ReservasPortal({ activeCondo, unidades }) {
  const [areas,        setAreas]        = useState([]);
  const [areaAtiva,    setAreaAtiva]    = useState(null);
  const [calendario,   setCalendario]   = useState(null);
  const [minhasRes,    setMinhasRes]    = useState([]);
  const [view,         setView]         = useState("areas"); // "areas" | "calendario" | "nova" | "minhas"
  const [mesAtual,     setMesAtual]     = useState(() => {
    const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`;
  });
  const [loadingCal,   setLoadingCal]   = useState(false);
  const [form,         setForm]         = useState({ dtReserva:"", hrInicio:"", hrFim:"", idUnidade:"", observacao:"" });
  const [saving,       setSaving]       = useState(false);
  const [erro,         setErro]         = useState("");
  const [sucesso,      setSucesso]      = useState("");

  // Carrega áreas e minhas reservas
  const idCondoAtivo  = activeCondo?.idCondominio;
  const idUnidadeAtiva = activeCondo?.idUnidade;
  useEffect(() => {
    if (!idCondoAtivo) return;
    setAreas([]);
    setMinhasRes([]);
    setAreaAtiva(null);
    setCalendario(null);
    setView("areas");
    portalService.areasComuns(idCondoAtivo).then(r => setAreas(r.data||[])).catch(()=>{});
    portalService.reservas(idCondoAtivo, idUnidadeAtiva).then(r => setMinhasRes(r.data||[])).catch(()=>{});
  }, [idCondoAtivo, idUnidadeAtiva]);

  // Carrega calendário quando mês/área muda
  const carregarCalendario = useCallback(async (idArea, mes) => {
    if (!idArea || !mes) return;
    setLoadingCal(true);
    try {
      const r = await portalService.calendarioArea(idArea, mes);
      setCalendario(r);
    } catch(e) { setErro(e.message); }
    setLoadingCal(false);
  }, []);

  useEffect(() => {
    if (areaAtiva && view === "calendario") carregarCalendario(areaAtiva.id, mesAtual);
  }, [areaAtiva, mesAtual, view, carregarCalendario]);

  const navegarMes = (dir) => {
    const [ano, mes] = mesAtual.split("-").map(Number);
    const nova = new Date(ano, mes - 1 + dir, 1);
    setMesAtual(`${nova.getFullYear()}-${String(nova.getMonth()+1).padStart(2,"0")}`);
  };

  // Constrói grade do calendário
  const buildCalendario = () => {
    if (!calendario) return [];
    const [ano, mes] = mesAtual.split("-").map(Number);
    const primeiroDia = new Date(ano, mes-1, 1).getDay();
    const ultimoDia   = new Date(ano, mes, 0).getDate();
    const reservasMap = {};
    (calendario.reservas||[]).forEach(r => {
      const d = r.dtReserva?.split("T")[0]||r.dtReserva;
      if (!reservasMap[d]) reservasMap[d] = [];
      reservasMap[d].push(r);
    });
    const bloqMap = {};
    (calendario.bloqueios||[]).forEach(b => {
      const ini = new Date(b.dtInicio+"T12:00:00");
      const fim = new Date(b.dtFim+"T12:00:00");
      for (let d = new Date(ini); d <= fim; d.setDate(d.getDate()+1)) {
        const key = d.toISOString().split("T")[0];
        bloqMap[key] = b.motivo;
      }
    });
    const dias = [];
    for (let i = 0; i < primeiroDia; i++) dias.push(null);
    for (let d = 1; d <= ultimoDia; d++) {
      const key = `${ano}-${String(mes).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      dias.push({ dia:d, key, reservas: reservasMap[key]||[], bloqueado: bloqMap[key]||null });
    }
    return dias;
  };

  const handleNovaReserva = async () => {
    setErro(""); setSucesso("");
    if (!form.dtReserva) return setErro("Selecione uma data.");
    if (!form.idUnidade && unidades.length > 1) return setErro("Selecione a unidade.");
    const idUnidade = form.idUnidade || unidades[0]?.idUnidade;
    setSaving(true);
    try {
      const r = await portalService.criarReserva({
        idArea: areaAtiva.id,
        idUnidade,
        dtReserva: form.dtReserva,
        hrInicio:  form.hrInicio || null,
        hrFim:     form.hrFim    || null,
        observacao: form.observacao || null,
      });
      setSucesso(r.message || "Reserva solicitada!");
      portalService.reservas(activeCondo?.idCondominio, activeCondo?.idUnidade).then(res => setMinhasRes(res.data||[])).catch(()=>{});
      carregarCalendario(areaAtiva.id, mesAtual);
      setForm({ dtReserva:"", hrInicio:"", hrFim:"", idUnidade:"", observacao:"" });
    } catch(e) { setErro(e.message); }
    setSaving(false);
  };

  const [confirmCancel, setConfirmCancel] = useState(null); // { id, mensagem, perigo }

  const handleCancelar = (id, reserva) => {
    let mensagem = "Confirma o cancelamento desta reserva?";
    let perigo = false;
    if (reserva) {
      const diasMin = reserva.diasCancelamento ?? 2;
      const diff = diffDays(reserva.dtReserva);
      const multa = parseFloat(reserva.vlrTaxaCancelamento || 0);
      if (reserva.status === "Confirmada" && multa > 0 && diff <= diasMin + 1) {
        mensagem = `Atenção: você ainda pode cancelar, mas será cobrada a taxa de cancelamento de R$ ${multa.toFixed(2).replace(".",",")}. Deseja continuar?`;
        perigo = true;
      }
    }
    setConfirmCancel({ id, mensagem, perigo });
  };

  const executarCancelamento = async () => {
    if (!confirmCancel) return;
    const id = confirmCancel.id;
    try {
      await portalService.cancelarReserva(id);
      setMinhasRes(prev => prev.map(r => r.id===id ? {...r, status:"Cancelada"} : r));
      setConfirmCancel(null);
      setSucesso("Reserva cancelada.");
    } catch(e) {
      setConfirmCancel(null);
      setErro(e.message || "Erro ao cancelar reserva.");
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  // Sub-navbar
  const NavBtn = ({ v, label, icon }) => (
    <button
      onClick={() => { setView(v); setErro(""); setSucesso(""); }}
      style={{
        padding:"6px 14px", borderRadius:20, fontSize:13, cursor:"pointer",
        border:"1.5px solid", fontWeight:view===v?700:400,
        borderColor:view===v?"var(--primary)":"var(--gray-200)",
        background:view===v?"var(--primary)":"#fff",
        color:view===v?"#fff":"var(--gray-600)",
        display:"flex", alignItems:"center", gap:6,
      }}>
      <i className={`bi ${icon}`}/>{label}
    </button>
  );

  return (
    <div>
      {/* Sub-nav */}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        <NavBtn v="areas"     label="Áreas Disponíveis" icon="bi-grid-3x3-gap"/>
        <NavBtn v="calendario" label="Calendário"        icon="bi-calendar3"/>
        <NavBtn v="nova"      label="Nova Reserva"      icon="bi-plus-circle"/>
        <NavBtn v="minhas"    label="Minhas Reservas"   icon="bi-list-check"/>
      </div>

      {/* ── ÁREAS ── */}
      {view==="areas" && (
        <div>
          {areas.length===0 ? (
            <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
              <i className="bi bi-building" style={{fontSize:36,display:"block",marginBottom:8}}/>
              Nenhuma área comum disponível
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
              {areas.map(a=>{
                const diasPerm = (a.diasPermitidos||"0,1,2,3,4,5,6").split(",").filter(Boolean);
                const todoDia  = diasPerm.length === 7;
                return (
                <div key={a.id}
                  onClick={()=>{ setAreaAtiva(a); setView("calendario"); }}
                  style={{background:"var(--bg-surface)",borderRadius:12,padding:16,cursor:"pointer",
                    border:"1.5px solid var(--gray-200)",transition:"all .15s",
                    boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="var(--primary)"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="var(--gray-200)"}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                    <div style={{width:40,height:40,borderRadius:10,background:"var(--bg-highlight)",
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <i className="bi bi-building-fill" style={{color:"var(--primary)",fontSize:18}}/>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:14}}>{a.nmArea}</div>
                      {a.capacidade && <div style={{fontSize:11,color:"var(--gray-400)"}}>Capacidade: {a.capacidade} pessoas</div>}
                    </div>
                  </div>
                  {a.descricao && <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:8}}>{a.descricao}</div>}

                  {/* Badges principais */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                    {a.vlrLocacao>0 ? (
                      <span style={{background:"#FEF3C7",color:"#92400E",padding:"2px 8px",borderRadius:8,fontSize:11,fontWeight:600}}>
                        R$ {parseFloat(a.vlrLocacao).toFixed(2).replace(".",",")}
                      </span>
                    ) : (
                      <span style={{background:"var(--bg-success-soft)",color:"#166534",padding:"2px 8px",borderRadius:8,fontSize:11,fontWeight:600}}>
                        Gratuita
                      </span>
                    )}
                    {parseFloat(a.vlrCaucao)>0 && (
                      <span style={{background:"var(--bg-warning-soft)",color:"#854D0E",padding:"2px 8px",borderRadius:8,fontSize:11,fontWeight:600}}>
                        Caução R$ {parseFloat(a.vlrCaucao).toFixed(2).replace(".",",")}
                      </span>
                    )}
                    <span style={{background:"var(--bg-highlight)",color:"#1D4ED8",padding:"2px 8px",borderRadius:8,fontSize:11}}>
                      {a.tpPeriodo==="DiaInteiro" ? "Dia inteiro" :
                        `${String(a.hrInicio||"").slice(0,5)} – ${String(a.hrFim||"").slice(0,5)}`}
                    </span>
                  </div>

                  {/* Regras compactas */}
                  <div style={{fontSize:11,color:"var(--gray-600)",lineHeight:1.6,borderTop:"1px solid #F1F5F9",paddingTop:8}}>
                    <div>
                      <i className="bi bi-calendar-week me-1" style={{color:"var(--gray-400)"}}/>
                      Dias: {todoDia ? "todos" : diasPerm.map(d=>DIAS_SEMANA[parseInt(d)]).join(", ")}
                    </div>
                    <div>
                      <i className="bi bi-hourglass-split me-1" style={{color:"var(--gray-400)"}}/>
                      Antecedência: mín {a.diasAntecedencia ?? 1}d · máx {a.diasMaxAntecedencia ?? 90}d
                    </div>
                    {parseFloat(a.vlrTaxaCancelamento)>0 && (
                      <div>
                        <i className="bi bi-x-octagon me-1" style={{color:"var(--gray-400)"}}/>
                        Cancelar até {a.diasCancelamento ?? 2}d antes — após isso, multa R$ {parseFloat(a.vlrTaxaCancelamento).toFixed(2).replace(".",",")}
                      </div>
                    )}
                    {parseInt(a.limiteReservasMes||0)>0 && (
                      <div>
                        <i className="bi bi-bar-chart me-1" style={{color:"var(--gray-400)"}}/>
                        Limite: {a.limiteReservasMes} reserva(s)/mês por unidade
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── CALENDÁRIO ── */}
      {view==="calendario" && (
        <div>
          {/* Seletor de área */}
          <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:16,flexWrap:"wrap"}}>
            <select className="form-select" style={{width:"auto",minWidth:180}}
              value={areaAtiva?.id||""} onChange={e=>{
                const a = areas.find(x=>String(x.id)===e.target.value);
                setAreaAtiva(a||null);
              }}>
              <option value="">Selecione a área</option>
              {areas.map(a=><option key={a.id} value={a.id}>{a.nmArea}</option>)}
            </select>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>navegarMes(-1)}>
                <i className="bi bi-chevron-left"/>
              </button>
              <span style={{fontWeight:700,fontSize:14,minWidth:130,textAlign:"center"}}>
                {MESES[parseInt(mesAtual.split("-")[1])-1]} {mesAtual.split("-")[0]}
              </span>
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>navegarMes(1)}>
                <i className="bi bi-chevron-right"/>
              </button>
            </div>
            {areaAtiva && (
              <button className="btn btn-primary btn-sm ms-auto"
                onClick={()=>{ setView("nova"); setForm(f=>({...f,dtReserva:""})); }}>
                <i className="bi bi-plus-lg me-1"/>Reservar
              </button>
            )}
          </div>

          {/* Grade do calendário */}
          {loadingCal ? (
            <div style={{textAlign:"center",padding:40}}>
              <span className="spinner-border text-primary"/>
            </div>
          ) : !areaAtiva ? (
            <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>Selecione uma área acima</div>
          ) : (
            <div>
              {/* Cabeçalho dias da semana */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2}}>
                {DIAS_SEMANA.map(d=>(
                  <div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,
                    color:"var(--gray-400)",padding:"4px 0"}}>{d}</div>
                ))}
              </div>
              {/* Dias */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
                {buildCalendario().map((dia,i)=>{
                  if (!dia) return <div key={`empty-${i}`}/>;
                  const hoje = new Date().toISOString().split("T")[0];
                  const isHoje = dia.key === hoje;
                  const isPast = dia.key < hoje;
                  const isBloq = !!dia.bloqueado;
                  const temReserva = dia.reservas.length > 0;
                  const minhasNoDia = dia.reservas.filter(r=>
                    unidades.some(u=>String(u.idUnidade)===String(r.idUnidade))
                  );
                  // Regras da área
                  const diasPerm = (areaAtiva?.diasPermitidos||"0,1,2,3,4,5,6").split(",").map(Number);
                  const diaSemana = new Date(dia.key+"T12:00:00").getDay();
                  const diaNaoPermitido = !diasPerm.includes(diaSemana);
                  const dd = diffDays(dia.key);
                  const foraDaJanela = !isPast && (
                    dd < (areaAtiva?.diasAntecedencia ?? 0) ||
                    (areaAtiva?.diasMaxAntecedencia && dd > areaAtiva.diasMaxAntecedencia)
                  );
                  const indisponivel = isPast || isBloq || diaNaoPermitido || foraDaJanela;
                  const motivoIndis  = isBloq ? `Bloqueado: ${dia.bloqueado}`
                    : isPast ? "Data passada"
                    : diaNaoPermitido ? `Fechado às ${DIAS_SEMANA[diaSemana]}s`
                    : foraDaJanela
                      ? (dd < (areaAtiva?.diasAntecedencia ?? 0)
                          ? `Antecedência mínima de ${areaAtiva?.diasAntecedencia ?? 0}d`
                          : `Acima da janela (${areaAtiva?.diasMaxAntecedencia}d)`)
                      : null;
                  return (
                    <div key={dia.key}
                      onClick={()=>{
                        if (!indisponivel && areaAtiva && !temReserva) {
                          setView("nova");
                          setForm(f=>({...f, dtReserva:dia.key}));
                        }
                      }}
                      title={motivoIndis || (temReserva?"Ocupado":"Disponível")}
                      style={{
                        borderRadius:8, padding:"6px 4px", minHeight:56,
                        textAlign:"center", cursor:indisponivel||temReserva?"default":"pointer",
                        background:isBloq?"#F1F5F9":diaNaoPermitido||foraDaJanela?"#FAFAFA":isPast?"#FAFAFA":temReserva?"#FEE2E2":"#F0FDF4",
                        border:`1.5px solid ${isHoje?"var(--primary)":isBloq?"#E2E8F0":isPast||diaNaoPermitido||foraDaJanela?"#F1F5F9":temReserva?"#FCA5A5":"#BBF7D0"}`,
                        opacity:isPast||diaNaoPermitido||foraDaJanela?0.55:1,
                        transition:"transform .1s",
                      }}
                      onMouseEnter={e=>{ if(!indisponivel&&!temReserva) e.currentTarget.style.transform="scale(1.05)"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.transform="scale(1)"; }}>
                      <div style={{fontWeight:isHoje?700:400,fontSize:13,
                        color:isHoje?"var(--primary)":isPast||diaNaoPermitido||foraDaJanela?"#94A3B8":"#1E293B"}}>
                        {dia.dia}
                      </div>
                      {isBloq && <div style={{fontSize:8,color:"var(--gray-500)",marginTop:2}}>Bloqueado</div>}
                      {!isBloq && diaNaoPermitido && <div style={{fontSize:8,color:"var(--gray-400)",marginTop:2}}>Fechado</div>}
                      {!isBloq && !diaNaoPermitido && foraDaJanela && <div style={{fontSize:8,color:"var(--gray-400)",marginTop:2}}>—</div>}
                      {!isBloq && !diaNaoPermitido && !foraDaJanela && temReserva && (
                        <div style={{fontSize:8,marginTop:2,color:"#991B1B",fontWeight:600}}>
                          {dia.reservas.length} reserva{dia.reservas.length>1?"s":""}
                        </div>
                      )}
                      {!indisponivel && !temReserva && (
                        <div style={{fontSize:8,marginTop:2,color:"#166534"}}>Livre</div>
                      )}
                      {minhasNoDia.length>0 && (
                        <div style={{fontSize:7,marginTop:1,background:"var(--primary)",
                          color:"#fff",borderRadius:4,padding:"1px 3px"}}>minha</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legenda */}
              <div style={{display:"flex",gap:12,marginTop:12,flexWrap:"wrap",fontSize:11}}>
                {[
                  {bg:"#F0FDF4",border:"#BBF7D0",label:"Disponível"},
                  {bg:"#FEE2E2",border:"#FCA5A5",label:"Ocupado"},
                  {bg:"#F1F5F9",border:"#E2E8F0",label:"Bloqueado"},
                  {bg:"#FAFAFA",border:"#F1F5F9",label:"Indisponível"},
                ].map(l=>(
                  <div key={l.label} style={{display:"flex",alignItems:"center",gap:4}}>
                    <div style={{width:14,height:14,borderRadius:3,
                      background:l.bg,border:`1px solid ${l.border}`}}/>
                    <span style={{color:"var(--gray-500)"}}>{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── NOVA RESERVA ── */}
      {view==="nova" && (
        <div style={{maxWidth:480}}>
          <h4 style={{color:"var(--primary)",marginBottom:16}}>
            <i className="bi bi-plus-circle me-2"/>Nova Reserva
          </h4>

          {erro  && <div className="alert alert-danger py-2"  style={{fontSize:13}}>{erro}</div>}
          {sucesso&&<div className="alert alert-success py-2" style={{fontSize:13}}>{sucesso}</div>}

          <div className="row g-3">
            {/* Área */}
            <div className="col-12">
              <label className="form-label fw-semibold">Área Comum</label>
              <select className="form-select"
                value={areaAtiva?.id||""} onChange={e=>{
                  setAreaAtiva(areas.find(a=>String(a.id)===e.target.value)||null);
                }}>
                <option value="">Selecione...</option>
                {areas.map(a=><option key={a.id} value={a.id}>{a.nmArea}</option>)}
              </select>
            </div>

            {/* Unidade (se tiver mais de 1) */}
            {unidades.length > 1 && (
              <div className="col-12">
                <label className="form-label fw-semibold">Unidade</label>
                <select className="form-select"
                  value={form.idUnidade} onChange={e=>setForm(f=>({...f,idUnidade:e.target.value}))}>
                  <option value="">Selecione...</option>
                  {unidades.map(u=>(
                    <option key={u.idUnidade} value={u.idUnidade}>
                      Un. {u.nmUnidade}{u.bloco?` / Bl. ${u.bloco}`:""} — {u.nmFantasia||u.nmCondominio}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Data com janela mín/máx da área */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">Data da Reserva</label>
              <input type="date" className="form-control"
                min={areaAtiva ? addDays(new Date(), areaAtiva.diasAntecedencia ?? 0) : new Date().toISOString().split("T")[0]}
                max={areaAtiva && areaAtiva.diasMaxAntecedencia ? addDays(new Date(), areaAtiva.diasMaxAntecedencia) : undefined}
                value={form.dtReserva}
                onChange={e=>setForm(f=>({...f,dtReserva:e.target.value}))}/>
              {areaAtiva && (
                <small style={{fontSize:11,color:"var(--gray-400)"}}>
                  Permitido entre {fmtD(addDays(new Date(), areaAtiva.diasAntecedencia ?? 0))} e {fmtD(addDays(new Date(), areaAtiva.diasMaxAntecedencia ?? 90))}
                </small>
              )}
            </div>

            {/* Horários (se for tpPeriodo=Horario) */}
            {areaAtiva?.tpPeriodo==="Horario" && (<>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Início</label>
                <input type="time" className="form-control"
                  value={form.hrInicio}
                  onChange={e=>setForm(f=>({...f,hrInicio:e.target.value}))}/>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Fim</label>
                <input type="time" className="form-control"
                  value={form.hrFim}
                  onChange={e=>setForm(f=>({...f,hrFim:e.target.value}))}/>
              </div>
            </>)}

            {/* Observação */}
            <div className="col-12">
              <label className="form-label fw-semibold">Observação</label>
              <textarea className="form-control" rows={2}
                placeholder="Informações adicionais..."
                value={form.observacao}
                onChange={e=>setForm(f=>({...f,observacao:e.target.value}))}/>
            </div>

            {/* Resumo da reserva (preview antes de enviar) */}
            {areaAtiva && form.dtReserva && (
              <div className="col-12">
                <div style={{background:"#F0F9FF",border:"1px solid #BAE6FD",borderRadius:8,padding:"12px 14px",fontSize:13,color:"#0C4A6E"}}>
                  <div style={{fontWeight:700,marginBottom:6}}>
                    <i className="bi bi-clipboard-check me-1"/>Confira antes de solicitar
                  </div>
                  <div style={{lineHeight:1.7}}>
                    <div><strong>Área:</strong> {areaAtiva.nmArea}</div>
                    <div><strong>Data:</strong> {fmtD(form.dtReserva)}{
                      areaAtiva.tpPeriodo==="Horario" && form.hrInicio
                        ? ` · ${form.hrInicio}${form.hrFim?` – ${form.hrFim}`:""}`
                        : " · dia inteiro"
                    }</div>
                    {parseFloat(areaAtiva.vlrLocacao)>0 && (
                      <div><strong>Locação:</strong> R$ {parseFloat(areaAtiva.vlrLocacao).toFixed(2).replace(".",",")}</div>
                    )}
                    {parseFloat(areaAtiva.vlrCaucao)>0 && (
                      <div><strong>Caução:</strong> R$ {parseFloat(areaAtiva.vlrCaucao).toFixed(2).replace(".",",")} (devolvida após o evento)</div>
                    )}
                    {parseFloat(areaAtiva.vlrLocacao)===0 && parseFloat(areaAtiva.vlrCaucao)===0 && (
                      <div style={{color:"#166534"}}><strong>Gratuita</strong></div>
                    )}
                    {parseFloat(areaAtiva.vlrTaxaCancelamento)>0 && (
                      <div style={{color:"#854D0E",marginTop:4,fontSize:12}}>
                        <i className="bi bi-exclamation-triangle me-1"/>
                        Cancelamento até <strong>{areaAtiva.diasCancelamento ?? 2} dia(s)</strong> antes — depois disso, taxa de R$ {parseFloat(areaAtiva.vlrTaxaCancelamento).toFixed(2).replace(".",",")}.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Regulamento completo */}
            {areaAtiva?.regulamento && (
              <div className="col-12">
                <details>
                  <summary style={{cursor:"pointer",color:"var(--primary)",fontSize:12,fontWeight:600}}>
                    <i className="bi bi-file-text me-1"/>Ler regulamento completo
                  </summary>
                  <div style={{marginTop:8,background:"var(--bg-surface-alt)",borderRadius:8,padding:"12px 14px",
                    color:"#374151",fontSize:12,whiteSpace:"pre-wrap",lineHeight:1.6,border:"1px solid var(--border-color)"}}>
                    {areaAtiva.regulamento}
                  </div>
                </details>
              </div>
            )}

            <div className="col-12 d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={()=>{ setView("areas"); setErro(""); setSucesso(""); }}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleNovaReserva} disabled={saving||!!sucesso}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-2"/>Enviando...</>
                  : <><i className="bi bi-check-circle me-1"/>Solicitar Reserva</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MINHAS RESERVAS ── */}
      {view==="minhas" && (
        <div>
          {minhasRes.length===0 ? (
            <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
              <i className="bi bi-calendar-x" style={{fontSize:36,display:"block",marginBottom:8}}/>
              Nenhuma reserva encontrada
            </div>
          ) : (
            <div>
              {minhasRes.map(r=>{
                const sc = statusColor[r.status]||{bg:"#F1F5F9",txt:"#64748B"};
                return (
                  <div key={r.id} style={{background:"var(--bg-surface)",borderRadius:12,padding:14,
                    marginBottom:10,border:"1px solid var(--gray-200)",
                    boxShadow:"0 1px 3px rgba(0,0,0,.05)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:14,color:"var(--text-primary)"}}>
                          {r.nmArea||r.nmAreaComum}
                        </div>
                        <div style={{fontSize:12,color:"var(--gray-500)",marginTop:3}}>
                          <i className="bi bi-calendar3 me-1"/>{fmtD(r.dtReserva)}
                          {r.hrInicio && <> · <i className="bi bi-clock me-1"/>{r.hrInicio}{r.hrFim?` – ${r.hrFim}`:""}</>}
                        </div>
                        {r.nmUnidade && (
                          <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2}}>
                            Unidade {r.nmUnidade}{r.bloco?`/Bl.${r.bloco}`:""}
                          </div>
                        )}
                        {r.vlrLocacao > 0 && (
                          <div style={{fontSize:11,color:"#854D0E",marginTop:2}}>
                            Taxa: R$ {parseFloat(r.vlrLocacao).toFixed(2).replace(".",",")}
                          </div>
                        )}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                        <span style={{background:sc.bg,color:sc.txt,padding:"3px 10px",
                          borderRadius:10,fontSize:11,fontWeight:700}}>{r.status}</span>
                        {(()=>{
                          if (!["Pendente","Confirmada"].includes(r.status)) return null;
                          const diasMin = r.diasCancelamento ?? 2;
                          const diff = diffDays(r.dtReserva);
                          // Contagem regressiva
                          const showCountdown = diff > 0;
                          if (r.status === "Confirmada" && diff <= diasMin) {
                            return (
                              <span style={{fontSize:10,color:"var(--gray-400)",fontStyle:"italic",textAlign:"right",lineHeight:1.3}}>
                                Cancelamento indisponível<br/>(prazo de {diasMin}d encerrado)
                              </span>
                            );
                          }
                          return (
                            <>
                              {showCountdown && (
                                <span style={{fontSize:10,color:"var(--gray-500)"}}>
                                  Faltam <strong>{diff} dia(s)</strong>
                                  {r.status==="Confirmada" && parseFloat(r.vlrTaxaCancelamento||0)>0 && (
                                    <> · cancela até {diasMin}d antes</>
                                  )}
                                </span>
                              )}
                              <button className="btn btn-outline-danger btn-sm"
                                style={{fontSize:11,padding:"2px 10px"}}
                                onClick={()=>handleCancelar(r.id, r)}>
                                <i className="bi bi-x me-1"/>Cancelar
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {confirmCancel && (
        <ConfirmModal
          titulo="Cancelar reserva"
          mensagem={confirmCancel.mensagem}
          textoOk="Sim, cancelar"
          textoCancelar="Voltar"
          perigo={confirmCancel.perigo}
          onConfirm={executarCancelamento}
          onCancel={()=>setConfirmCancel(null)}
        />
      )}
    </div>
  );
}
